import React, { useState, useEffect, useRef, FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dialog, Empty, PullToRefresh, SearchBar, Space, SwipeAction, Toast } from 'antd-mobile';
import { SwipeActionRef } from 'antd-mobile/es/components/swipe-action';
import { AddCircleOutline, DeleteOutline, EditSOutline, SearchOutline } from 'antd-mobile-icons';
import { PullStatus } from 'antd-mobile/es/components/pull-to-refresh';
import dayjs from 'dayjs';

// Import components
import { LoadingComponent } from '../ui';
// Import hooks and utilities
import { useNavbar } from '../../../hooks/use-navbar';
import { useClient } from '../../../hooks/use-client';
import { monthYear } from '../../../utils';
// Import from new structure
import { InterventionJson, PrestationEnum, prestationKeys } from '../../../types/models';
import { apiClient } from '../../../api';

import { ExploitationCard } from './ExploitationCard';

interface Filters {
  search: string;
  buttonsVTypeSelected: { [key: string]: boolean };
  buttonsPrestationSelected: {
    exterior?: boolean;
    disinfection?: boolean;
    interior?: boolean;
    decontamination?: boolean;
    package?: boolean;
  };
  selectedDate?: string;
}

const PRESTA_FILTER_LABELS: Record<PrestationEnum, string> = {
  exterior: 'Ext',
  interior: 'Int',
  disinfection: 'Desinf',
  package: 'Pack',
  decontamination: 'Dec',
};
const PRESTA_FILTER_CLASS: Record<PrestationEnum, string> = {
  exterior: 'presta-ext',
  interior: 'presta-int',
  disinfection: 'presta-des',
  package: 'presta-pack',
  decontamination: 'presta-dec',
};

const PRESTA_LABELS: Record<PrestationEnum, string> = {
  exterior: 'Ext',
  interior: 'Int',
  disinfection: 'Desinf',
  package: 'Pack',
  decontamination: 'Dec',
};
const PRESTA_CLASS: Record<PrestationEnum, string> = {
  exterior: 'ext',
  interior: 'int',
  disinfection: 'des',
  package: 'pack',
  decontamination: 'dec',
};

const InterventionRow: FC<{ intervention: InterventionJson; onDelete: () => void }> = ({
  intervention: iv,
  onDelete,
}) => {
  const navigate = useNavigate();
  const swipeRef = useRef<SwipeActionRef>(null);
  const time = iv.timestamp ? dayjs(iv.timestamp).format('HH:mm') : '';
  const date = iv.timestamp ? dayjs(iv.timestamp).format('DD/MM') : '';
  return (
    <SwipeAction
      ref={swipeRef}
      rightActions={[
        {
          key: 'edit',
          text: <EditSOutline fontSize={20} />,
          color: 'warning',
          onClick: () => {
            swipeRef.current?.close();
            navigate(`/client/${iv.client_id}/intervention/${iv.id}`);
          },
        },
      ]}
      leftActions={[
        {
          key: 'delete',
          text: <DeleteOutline fontSize={20} />,
          color: 'danger',
          onClick: async () => {
            await Dialog.confirm({
              cancelText: 'Annuler',
              confirmText: 'Supprimer',
              content: "Supprimer l'intervention ?",
              onConfirm: () => {
                apiClient.interventions.deleteIntervention(iv.id).then((res: { affected: number }) => {
                  if (res.affected === 1) {
                    onDelete();
                    Toast.show({ icon: 'success', content: 'Interv. supprimée' });
                  }
                });
              },
            });
            swipeRef.current?.close();
          },
        },
      ]}
    >
      <div className="intervention-table-row">
        <div className="col-type">{iv.prestation_costs?.title || '—'}</div>
        <div className="col-immat">
          {iv.immatriculation || '—'}
          {iv.immatriculation_bis && (
            <span className="bis">+{iv.immatriculation_bis}</span>
          )}
        </div>
        <div className="col-parc">
          {iv.parc_number || '—'}
          {iv.parc_number_bis && (
            <span className="bis">+{iv.parc_number_bis}</span>
          )}
        </div>
        <div className="col-presta">
          {prestationKeys
            .filter((k) => iv[k])
            .map((k) => (
              <span key={k} className={`pchip ${PRESTA_CLASS[k]}`}>
                {PRESTA_LABELS[k]}
              </span>
            ))}
        </div>
        <div className="col-date">
          {date}
          <span className="time">{time}</span>
        </div>
      </div>
    </SwipeAction>
  );
};

const Client: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const { clientId } = useParams<'clientId'>();

  // Always call hooks at the top level
  const data = useClient(clientId ? +clientId : 0);
  const client = data.client;
  const [loading, setLoading] = useState(true);
  const [interventions, setInterventions] = useState<InterventionJson[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    search: '',
    buttonsVTypeSelected: {},
    buttonsPrestationSelected: {},
  });

  const onClickSearch = (): void => {
    setFilters({ search: '', buttonsVTypeSelected: {}, buttonsPrestationSelected: {}, selectedDate: undefined });
    setShowSearch((showSearch) => !showSearch);
  };
  const fetchInterventions = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await apiClient.interventions.getInterventions({
        filter: `client_id||eq||${clientId}`,
      });
      setInterventions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching interventions:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (client) {
      fetchInterventions();
    }
  }, [client]);

  useEffect(() => {
    if (!navbar || !client) {
      return;
    }

    navbar.setTitle(client.name);
    navbar.setBackArrow(true);
    navbar.setOnBack(() => navigate(-1));
    navbar.setRight(
      <div style={{ fontSize: 24 }}>
        <Space style={{ '--gap': '16px' }}>
          <SearchOutline onClick={onClickSearch} />
        </Space>
      </div>,
    );
  }, [client]);

  if (!clientId) {
    return <div>Client introuvable</div>;
  }

  const statusRecord: Record<PullStatus, string> = {
    pulling: 'Recharger',
    canRelease: 'Lacher pour recharger',
    refreshing: 'Chargement...',
    complete: 'Terminée',
  };

  const typesOfVehicle = [...new Set(interventions.map((intervention) => intervention.prestation_costs.title))];
  const distinctPrestation = interventions.reduce(
    (acc, intervention) => {
      acc.exterior = acc.exterior || intervention.exterior;
      acc.disinfection = acc.disinfection || intervention.disinfection;
      acc.interior = acc.interior || intervention.interior;
      acc.decontamination = acc.decontamination || intervention.decontamination;
      acc.package = acc.package || intervention.package;
      return acc;
    },
    {
      exterior: false,
      disinfection: false,
      interior: false,
      decontamination: false,
      package: false,
    },
  );

  const interventionFiltered = interventions.filter((i) => {
    // exclude by search string
    if (filters.search.length) {
      const parcNumberMatch = i?.parc_number.toLowerCase().includes(filters.search.toLowerCase());
      const immatMatch = i?.immatriculation.toLowerCase().includes(filters.search.toLowerCase());
      if (!parcNumberMatch && !immatMatch) {
        return false;
      }
    }
    // exclude by prestation
    if (
      (filters.buttonsPrestationSelected.exterior && !i.exterior) ||
      (filters.buttonsPrestationSelected.disinfection && !i.disinfection) ||
      (filters.buttonsPrestationSelected.interior && !i.interior) ||
      (filters.buttonsPrestationSelected.decontamination && !i.decontamination) ||
      (filters.buttonsPrestationSelected.package && !i.package)
    ) {
      return false;
    }

    if (filters.selectedDate) {
      const ivDate = (i.timestamp || '').slice(0, 10);
      if (ivDate !== filters.selectedDate) {
        return false;
      }
    }

    const isTypeSelected = Object.keys(filters.buttonsVTypeSelected).reduce((acc, t) => {
      // t = VUL
      return acc && (!filters.buttonsVTypeSelected[t] || i.prestation_costs.title === t);
    }, true);

    return isTypeSelected;
  });

  // Dates uniques avec interventions (DESC, plus recent en premier)
  const interventionDates = Array.from(
    interventions.reduce<Map<string, number>>((acc, iv) => {
      const d = (iv.timestamp || '').slice(0, 10);
      if (d) {
        acc.set(d, (acc.get(d) || 0) + 1);
      }
      return acc;
    }, new Map()),
  ).sort((a, b) => (a[0] < b[0] ? 1 : -1));

  const months = interventionFiltered.reduce<string[]>((acc, intervention) => {
    if (!acc.includes(monthYear(intervention))) {
      acc.push(monthYear(intervention));
    }
    return acc;
  }, []);

  if (loading) {
    return <LoadingComponent />;
  }
  return (
    <>
      {/* Commentaire exploitation defini dans admin.camionnet.fr */}
      {client?.intervention_comment && client.intervention_comment.trim() && (
        <div
          style={{
            margin: '10px 8px 12px',
            padding: '12px 14px',
            background:
              'linear-gradient(135deg, rgba(245,158,11,0.14), rgba(245,158,11,0.04))',
            border: '1px solid rgba(245,158,11,0.45)',
            borderLeft: '4px solid #F59E0B',
            borderRadius: 12,
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
          }}
        >
          <span
            style={{
              fontSize: 20,
              lineHeight: 1,
              flexShrink: 0,
              marginTop: 1,
            }}
          >
            📋
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#F59E0B',
                marginBottom: 4,
              }}
            >
              Consigne exploitation
            </div>
            <div
              style={{
                fontSize: 13.5,
                color: 'var(--text-primary)',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
              }}
            >
              {client.intervention_comment}
            </div>
          </div>
        </div>
      )}

      {client && <ExploitationCard client={client} />}

      {showSearch && (
        <div className="client-search-panel">
          <SearchBar
            placeholder="Immat. Parc n°"
            onChange={(val) => setFilters({ ...filters, search: val })}
            onClear={() => setFilters({ ...filters, search: '' })}
          />

          {typesOfVehicle.length > 0 && (
            <div className="filter-block">
              <div className="filter-label">Type de véhicule</div>
              <div className="chip-row">
                {typesOfVehicle.map((t) => {
                  const isSelected = !!(t in filters.buttonsVTypeSelected && filters.buttonsVTypeSelected[t]);
                  return (
                    <button
                      type="button"
                      key={t}
                      className={`filter-chip ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        const newFilters = { ...filters };
                        newFilters.buttonsVTypeSelected = {
                          ...newFilters.buttonsVTypeSelected,
                          [t]: !isSelected,
                        };
                        setFilters(newFilters);
                      }}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {(Object.keys(distinctPrestation) as Array<PrestationEnum>).some((k) => distinctPrestation[k]) && (
            <div className="filter-block">
              <div className="filter-label">Prestations</div>
              <div className="chip-row">
                {(Object.keys(distinctPrestation) as Array<PrestationEnum>).map((t) => {
                  if (!distinctPrestation[t]) {
                    return null;
                  }
                  const isSelected: boolean = filters.buttonsPrestationSelected[t] ?? false;
                  return (
                    <button
                      type="button"
                      key={t}
                      className={`filter-chip ${PRESTA_FILTER_CLASS[t]} ${isSelected ? 'active' : ''}`}
                      onClick={() => {
                        const newFilters = { ...filters };
                        newFilters.buttonsPrestationSelected = {
                          ...newFilters.buttonsPrestationSelected,
                          [t]: !isSelected,
                        };
                        setFilters(newFilters);
                      }}
                    >
                      {PRESTA_FILTER_LABELS[t]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {interventionDates.length > 0 && (
            <div className="filter-block">
              <div className="filter-label">Date d&rsquo;intervention</div>
              <div className="chip-row scroll-x">
                <button
                  type="button"
                  className={`filter-chip ${!filters.selectedDate ? 'active' : ''}`}
                  onClick={() => setFilters({ ...filters, selectedDate: undefined })}
                >
                  Toutes
                </button>
                {interventionDates.map(([date, count]) => {
                  const isSelected = filters.selectedDate === date;
                  return (
                    <button
                      type="button"
                      key={date}
                      className={`filter-chip ${isSelected ? 'active' : ''}`}
                      onClick={() => setFilters({ ...filters, selectedDate: isSelected ? undefined : date })}
                    >
                      {dayjs(date).format('DD/MM')}
                      <span className="count">·{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      {months.length === 0 ? (
        <Empty />
      ) : (
        <div style={{ height: '100%' }}>
          <PullToRefresh
            renderText={(status) => {
              return <div>{statusRecord[status]}</div>;
            }}
            onRefresh={async () => {
              if (client) {
                fetchInterventions();
              }
            }}
          >
            {months.map((month) => {
              const rows = interventionFiltered.filter((i) => monthYear(i) === month);
              return (
                <div key={month}>
                  <div className="intervention-month-label">{month}</div>
                  <div className="intervention-table-wrap">
                    <div className="intervention-table-header">
                      <span>Type véhicule</span>
                      <span>Immat.</span>
                      <span>Parc</span>
                      <span>Prestations</span>
                      <span>Date / heure</span>
                    </div>
                    {rows.map((iv) => (
                      <InterventionRow
                        key={iv.id}
                        intervention={iv}
                        onDelete={() => {
                          setInterventions((prev) => prev.filter((i) => i.id !== iv.id));
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
            {/* La barre fixe du bon flotte au-dessus de la liste : sans cette
                marge, elle masquerait la derniere intervention. */}
            <div style={{ height: 'calc(150px + env(safe-area-inset-bottom, 0px))' }} />
          </PullToRefresh>
        </div>
      )}
      {/* Fin de prestation : le bon d'intervention a signer. Barre fixe au-dessus
          de la barre d'onglets — toujours atteignable, meme apres une longue
          liste d'interventions. */}
      <button
        type="button"
        onClick={() => navigate(`/client/${client?.id}/bon`)}
        style={{
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: 'calc(74px + env(safe-area-inset-bottom, 0px))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '15px',
          background: 'var(--navy)',
          color: 'var(--teal)',
          border: '1px solid var(--teal)',
          borderRadius: 12,
          fontSize: 15,
          fontWeight: 700,
          letterSpacing: '0.03em',
          boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
          cursor: 'pointer',
          zIndex: 998,
        }}
      >
        Bon d&apos;intervention à signer
      </button>
      {/* Bouton flottant "+" : ancien FloatingBubble antd-mobile remplace
          par un bouton position: fixed classique pour eviter les bugs
          de clic intermittents sur iOS Safari / PWA. */}
      <button
        type="button"
        aria-label="Nouvelle intervention"
        onClick={() => navigate(`/client/${client?.id}/new-choice`)}
        style={{
          position: 'fixed',
          bottom: 'calc(146px + env(safe-area-inset-bottom, 0px))',
          right: 24,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'var(--teal)',
          color: 'var(--navy)',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(49,220,200,0.45), 0 2px 6px rgba(0,0,0,0.2)',
          cursor: 'pointer',
          zIndex: 999,
          WebkitTapHighlightColor: 'transparent',
          touchAction: 'manipulation',
        }}
      >
        <AddCircleOutline fontSize={32} />
      </button>
    </>
  );
};

export default Client;
